import * as Yup from "yup";
import * as _ from "lodash";

import { useTranslation, Enterprise } from "@ctra/i18n";
import { TaskListFormValues, GamePlanFormValues } from "@ctra/api";
import { UnitSystem } from "@ctra/utils";

const {
  gamePlan: {
    wizard: {
      form: { validation }
    }
  }
} = Enterprise;

type ValidationSchema = Yup.SchemaOf<
  Omit<
    GamePlanFormValues & {
      taskList: Array<Omit<TaskListFormValues, "dueDateMoment">>;
    },
    "description"
  >
>;

/**
 * Simple validation schema generator hook for game plans.
 * It wraps around the useTranslation hook and enables
 * `t` in validation messages.
 * @todo think if there is a more elegant solution with Yup.setLocale
 */
export const useValidationSchema = (): (() => ValidationSchema) => {
  const { t } = useTranslation();

  /**
   * Validation schema for game plan tasks
   */
  const gamePlanTaskValidationSchema: Yup.SchemaOf<Omit<TaskListFormValues, "dueDateMoment">> =
    Yup.object().shape({
      id: Yup.string(),
      label: Yup.string().defined(t<string>(validation.taskList.label)),
      status: Yup.number().defined(),
      assignedTo: Yup.string().nullable(),
      createdBy: Yup.string()
    });

  const validationSchema = Yup.object().shape({
    id: Yup.string(),
    title: Yup.string().defined(t<string>(validation.title)),
    dataDescriptorID: Yup.string().typeError(t<string>(validation.dataDescriptorID)),
    farmID: Yup.number().typeError(t<string>(validation.farmID)).defined(t<string>(validation.farmID)),
    hasTarget: Yup.number().min(0).max(1).defined(),
    goal: Yup.number(),
    taskList: Yup.array().of(gamePlanTaskValidationSchema).defined(),
    timePeriod: Yup.array<moment.Moment>()
      .test({
        test: (dates) => _.every(dates, (date) => date && date.isValid()),
        message: t<string>(validation.timePeriod)
      })
      .defined(),
    unitSystem: Yup.string().oneOf(Object.values(UnitSystem)).defined()
  });

  return () => validationSchema;
};
